import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';

export default function OwnerCarrySection(props) {
  const { control, FloatingNumber, getVal, setValue, handleCurrencyBlur, salesPriceFormattedTick, onSalesPriceBlur } = props;

  // local helpers for visibility
  const owner1 = getVal('loanInfoMeta.allowOwnerCarry1Check', true);
  const owner2 = getVal('loanInfoMeta.allowOwnerCarry2Check', false);

  // When this section is shown (buyersLoanType === 'Owner Carry'),
  // enforce defaults: first owner-carry checkbox checked, second unchecked.
  useEffect(() => {
    try {
      setValue && setValue('loanInfoMeta.allowOwnerCarry1Check', true, { shouldDirty: true, shouldTouch: true });
      setValue && setValue('loanInfoMeta.allowOwnerCarry2Check', false, { shouldDirty: true, shouldTouch: true });
    } catch (err) {
      // ignore
    }
  }, [setValue]);

  // helper to parse numeric from formatted string
  const parseNumber = (v) => Number(String(v || '').replace(/[^0-9.-]+/g, '')) || 0;

  // Recompute amounts when the sales price formatting/blur event occurs in the parent.
  // The parent increments `salesPriceFormattedTick` on salesPrice blur which triggers this effect.
  useEffect(() => {
    try {
      const salesPriceRaw = getVal('salesPrice', '') || '';
      const spNum = parseNumber(salesPriceRaw);
      if (!spNum || spNum <= 0) return;

      // amount (1st loan) — only auto-calc when owner1 enabled
      const perc1Raw = getVal('loanInfoMeta.conventionalPercent', '') || '';
      const perc1 = parseNumber(perc1Raw);
      const allow1 = getVal('loanInfoMeta.allowOwnerCarry1Check', false);
      if (allow1 && perc1 >= 0) {
        const amt = (spNum * perc1) / 100;
        setValue && setValue('loanInfoMeta.amount', amt.toFixed(2), { shouldDirty: true, shouldTouch: true });
      }

      // amount2 — only auto-calc when owner2 enabled
      const perc2Raw = getVal('loanInfoMeta.conventionalPercent2', '') || '';
      const perc2 = parseNumber(perc2Raw);
      const allow2 = getVal('loanInfoMeta.allowOwnerCarry2Check', false);
      if (allow2 && perc2 >= 0) {
        const amt2 = (spNum * perc2) / 100;
        setValue && setValue('loanInfoMeta.amount2', amt2.toFixed(2), { shouldDirty: true, shouldTouch: true });
      }
    } catch (err) {
      // ignore
    }
    // ensure adjusted is correct after sales price driven recalculation
    //try { updateAdjustedAndNotify(); } catch (err) { /* ignore */ }
    // salesPriceFormattedTick is changed by parent on salesPrice blur/format
  }, [salesPriceFormattedTick, getVal, setValue]);

  // helper: set loanInfoMeta.adjusted according to owner carry checkboxes
  // precedence: owner2 takes precedence over owner1 when both checked
  const updateAdjustedAndNotify = () => {
    try {
      const allow1 = getVal('loanInfoMeta.allowOwnerCarry1Check', false);
      const allow2 = getVal('loanInfoMeta.allowOwnerCarry2Check', false);
      const amt = getVal('loanInfoMeta.amount', '0.00') || '0.00';
      const amt2 = getVal('loanInfoMeta.amount2', '0.00') || '0.00';
      let adjusted = '0.00';
      if (allow2) adjusted = amt2;
      else if (allow1) adjusted = amt;

      // avoid unnecessary writes / parent notifications which can trigger loops
      const prev = getVal('loanInfoMeta.adjusted', '0.00') || '0.00';
      // compare as strings to avoid minor type mismatches
      if (String(prev) !== String(adjusted)) {
        try { setValue && setValue('loanInfoMeta.adjusted', adjusted, { shouldDirty: true, shouldTouch: true }); } catch (err) { /* ignore */ }
        // call parent handler so derived totals/arrays recalc — only when changed
        try { typeof onSalesPriceBlur === 'function' && onSalesPriceBlur(); } catch (err) { /* ignore */ }
      }
      // otherwise do nothing to avoid loops
    } catch (err) {
      // ignore
    }
  };

  return (
    <>
      <div className="calc-frm-field field-baseline">
        {/* First Left division */}
        <div className="calc-frm-field-division">
          {/* Conditional  based on loan type */}
          <div className="calc-frm-fld form-group">
            <Controller
              name="loanInfoMeta.allowOwnerCarry1Check"
              control={control}
              defaultValue={!!getVal('loanInfoMeta.allowOwnerCarry1Check', false)}
              render={({ field }) => (
                <p>
                  <input
                    type="checkbox"
                    checked={!!field.value}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      try {
                        field.onChange && field.onChange(checked);
                          setValue && setValue('loanInfoMeta.allowOwnerCarry1Check', checked, { shouldDirty: true, shouldTouch: true });
                          const salesPriceRaw = getVal('salesPrice', '') || '';
                          const spNum = parseNumber(salesPriceRaw);
                          if (checked) {
                            // when enabling owner1, compute amount if conventionalPercent present
                            const percRaw = getVal('loanInfoMeta.conventionalPercent', '') || '';
                            const perc = parseNumber(percRaw);
                            if (spNum > 0 && perc > 0) {
                              const amt = (spNum * perc) / 100;
                              setValue && setValue('loanInfoMeta.amount', amt.toFixed(2), { shouldDirty: true, shouldTouch: true });
                            }
                          } else {
                            // when disabling owner1, clear its percent and amount and also disable owner2 and clear its percent/amount
                            try {
                              setValue && setValue('loanInfoMeta.allowOwnerCarry2Check', false, { shouldDirty: true, shouldTouch: true });
                            } catch (err) { /* ignore */ }
                            try { setValue && setValue('loanInfoMeta.conventionalPercent', '80.00', { shouldDirty: true, shouldTouch: true }); } catch (err) { /* ignore */ }
                            try { setValue && setValue('loanInfoMeta.amount', '0.00', { shouldDirty: true, shouldTouch: true }); } catch (err) { /* ignore */ }
                            try { setValue && setValue('loanInfoMeta.conventionalPercent2', '0.00', { shouldDirty: true, shouldTouch: true }); } catch (err) { /* ignore */ }
                            try { setValue && setValue('loanInfoMeta.amount2', '0.00', { shouldDirty: true, shouldTouch: true }); } catch (err) { /* ignore */ }
                          }
                          // update adjusted and notify parent
                          try { updateAdjustedAndNotify(); } catch (err) { /* ignore */ }
                      } catch (err) { /* ignore */ }
                    }}
                  />
                  <b>Owner Carry</b>
                </p>
              )}
            />
          </div>

          {/* 1st Loan */}
          {owner1 && (
            <>
              <div className="calc-frm-fld form-group">
                <span><b>1st Loan</b></span>
              </div>

              {/* Loans  Percentage 1*/}
              <div className="calc-frm-fld form-group">
                  <FloatingNumber
                  type="text"
                  name="loanInfoMeta.conventionalPercent"
                  value={getVal('loanInfoMeta.conventionalPercent', '')}
                  control={control}
                  placeholder="0.00"
                  className="inpt-pla"
                  onChange={(e) => {
                    try {
                      const raw = e.target.value;
                      setValue && setValue('loanInfoMeta.conventionalPercent', raw, { shouldDirty: true, shouldTouch: true });
                    } catch (err) { /* ignore */ }
                  }}
                  onBlur={() => {
                    try {
                      // format the percent value first
                      handleCurrencyBlur && handleCurrencyBlur('loanInfoMeta.conventionalPercent');
                      // then compute amount from the (possibly formatted) percent value
                      const salesPriceRaw = getVal('salesPrice', '') || '';
                      const spNum = parseNumber(salesPriceRaw);
                      const percRaw = getVal('loanInfoMeta.conventionalPercent', '') || '';
                      const num = parseNumber(percRaw);
                      const allow1 = getVal('loanInfoMeta.allowOwnerCarry1Check', false);
                      if (allow1 && spNum > 0 && num >= 0) {
                        const amt = (spNum * num) / 100;
                        setValue && setValue('loanInfoMeta.amount', amt.toFixed(2), { shouldDirty: true, shouldTouch: true });
                        try { updateAdjustedAndNotify(); } catch (err) { /* ignore */ }
                      }
                    } catch (err) { /* ignore */ }
                  }}
                />
                <label>Loan to Value %</label>
              </div>
            </>
          )}

          {owner1 && (
            <>
              {/* Interest Rates 1 */}
              <div className="calc-frm-fld form-group">
                <FloatingNumber
                  type="text"
                  name="loanInfoMeta.interestRate1"
                  control={control}
                  placeholder="0.00"
                  className="inpt-pla"
                  value={getVal('loanInfoMeta.interestRate1', '')}
                  onChange={(e) => { try { setValue && setValue('loanInfoMeta.interestRate1', e.target.value, { shouldDirty: true, shouldTouch: true }); } catch (err) { /* ignore */ } }}
                  onBlur={() => handleCurrencyBlur('loanInfoMeta.interestRate1')}
                />
                <label>Interest Rate %</label>
              </div>

              {/* Terms 1 */}
              <div className="calc-frm-fld form-group">
                <FloatingNumber
                  type="text"
                  name="loanInfoMeta.termsInYears1"
                  control={control}
                  placeholder="0.00"
                  className="inpt-pla"
                  value={getVal('loanInfoMeta.termsInYears1', '')}
                  onChange={(e) => { try { setValue && setValue('loanInfoMeta.termsInYears1', e.target.value, { shouldDirty: true, shouldTouch: true }); } catch (err) { /* ignore */ } }}
                  onBlur={() => handleCurrencyBlur('loanInfoMeta.termsInYears1')}
                />
                <label>Terms in Years</label>
              </div>
            </>
          )}

          {/* Add Due Years */}
          {owner1 && (
            <div className="calc-frm-fld form-group">
              <FloatingNumber
                type="text"
                name="loanInfoMeta.addDueYear1"
                control={control}
                placeholder="0.00"
                className="inpt-pla"
                value={getVal('loanInfoMeta.addDueYear1', '')}
                onChange={(e) => { try { setValue && setValue('loanInfoMeta.addDueYear1', e.target.value, { shouldDirty: true, shouldTouch: true }); } catch (err) { /* ignore */ } }}
                onBlur={() => handleCurrencyBlur('loanInfoMeta.addDueYear1')}
              />
              <label>Add Due Years</label>
            </div>
          )}
        </div>

        {/* Second Right division */}
        <div className="calc-frm-field-division">
          <div className="calc-frm-fld form-group">
            <Controller
              name="loanInfoMeta.allowOwnerCarry2Check"
              control={control}
              defaultValue={!!getVal('loanInfoMeta.allowOwnerCarry2Check', false)}
              render={({ field }) => (
                <p>
                  <input
                    type="checkbox"
                    checked={!!field.value}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      try {
                        field.onChange && field.onChange(checked);
                        setValue && setValue('loanInfoMeta.allowOwnerCarry2Check', checked, { shouldDirty: true, shouldTouch: true });
                        const salesPriceRaw2 = getVal('salesPrice', '') || '';
                        const spNum2 = parseNumber(salesPriceRaw2);
                        if (checked) {
                          // compute amount2 immediately if conventionalPercent2 exists
                          const perc2Raw = getVal('loanInfoMeta.conventionalPercent2', '') || '';
                          const perc2 = parseNumber(perc2Raw);
                          if (spNum2 > 0 && perc2 > 0) {
                            const amt2 = (spNum2 * perc2) / 100;
                            setValue && setValue('loanInfoMeta.amount2', amt2.toFixed(2), { shouldDirty: true, shouldTouch: true });
                          }
                        } else {
                          // when disabling owner2, clear percent2 and amount2
                          try { setValue && setValue('loanInfoMeta.conventionalPercent2', '0.00', { shouldDirty: true, shouldTouch: true }); } catch (err) { /* ignore */ }
                          try { setValue && setValue('loanInfoMeta.amount2', '0.00', { shouldDirty: true, shouldTouch: true }); } catch (err) { /* ignore */ }
                        }
                        // update adjusted and notify parent
                        try { updateAdjustedAndNotify(); } catch (err) { /* ignore */ }
                      } catch (err) { /* ignore */ }
                    }}
                  />
                  <b>Owner Carry</b>
                </p>
              )}
            />
          </div>


          {owner1 && (
          <div className="calc-frm-fld form-group">
            <span><b>2nd Loan</b></span>
          </div>
          )}

          {/* Loans  Percentage 2: show only when owner1 is enabled (per UX rules). */}
          {owner1 && (
            <div className="calc-frm-fld form-group">
              <FloatingNumber
                type="text"
                name="loanInfoMeta.conventionalPercent2"
                value={getVal('loanInfoMeta.conventionalPercent2', '')}
                control={control}
                placeholder="0.00"
                className="inpt-pla"
                onChange={(e) => {
                  try {
                    const raw = e.target.value;
                    setValue && setValue('loanInfoMeta.conventionalPercent2', raw, { shouldDirty: true, shouldTouch: true });
                  } catch (err) { /* ignore */ }
                }}
                onBlur={() => {
                  try {
                    // format the percent2 value first
                    handleCurrencyBlur && handleCurrencyBlur('loanInfoMeta.conventionalPercent2');
                    // then compute amount2 from the (possibly formatted) percent2
                    const salesPriceRaw = getVal('salesPrice', '') || '';
                    const spNum = parseNumber(salesPriceRaw);
                    const perc2Raw = getVal('loanInfoMeta.conventionalPercent2', '') || '';
                    const num = parseNumber(perc2Raw);
                    const allow2 = getVal('loanInfoMeta.allowOwnerCarry2Check', false);
                    if (allow2 && spNum > 0 && num >= 0) {
                      const amt2 = (spNum * num) / 100;
                      setValue && setValue('loanInfoMeta.amount2', amt2.toFixed(2), { shouldDirty: true, shouldTouch: true });
                      try { updateAdjustedAndNotify(); } catch (err) { /* ignore */ }
                    }
                  } catch (err) { /* ignore */ }
                }}
              />
              <label>Loan to Value %</label>
            </div>
          )}

          {/* Interest Rates 2 */}
          {owner1 && owner2 && (
            <div className="calc-frm-fld form-group">
            <FloatingNumber
              type="text"
              name="loanInfoMeta.interestRate2"
              control={control}
              placeholder="0.00"
              className="inpt-pla"
              value={getVal('loanInfoMeta.interestRate2', '')}
              onChange={(e) => { try { setValue && setValue('loanInfoMeta.interestRate2', e.target.value, { shouldDirty: true, shouldTouch: true }); } catch (err) { /* ignore */ } }}
              onBlur={() => handleCurrencyBlur('loanInfoMeta.interestRate2')}
            />
            <label>Interest Rate %</label>
            </div>
          )}

          {/* Terms 2 */}
          {owner1 && owner2 && (
            <div className="calc-frm-fld form-group">
            <FloatingNumber
              type="text"
              name="loanInfoMeta.termsInYears2"
              control={control}
              placeholder="0.00"
              className="inpt-pla"
              value={getVal('loanInfoMeta.termsInYears2', '')}
              onChange={(e) => { try { setValue && setValue('loanInfoMeta.termsInYears2', e.target.value, { shouldDirty: true, shouldTouch: true }); } catch (err) { /* ignore */ } }}
              onBlur={() => handleCurrencyBlur('loanInfoMeta.termsInYears2')}
            />
            <label>Terms in Years</label>
            </div>
          )}

          {/* Add Due Years 2*/}
          {owner1 && owner2 && (
            <div className="calc-frm-fld form-group">
              <FloatingNumber
                type="text"
                name="loanInfoMeta.addDueYear2"
                control={control}
                placeholder="0.00"
                className="inpt-pla"
                value={getVal('loanInfoMeta.addDueYear2', '')}
                onChange={(e) => { try { setValue && setValue('loanInfoMeta.addDueYear2', e.target.value, { shouldDirty: true, shouldTouch: true }); } catch (err) { /* ignore */ } }}
                onBlur={() => handleCurrencyBlur('loanInfoMeta.addDueYear2')}
              />
              <label>Add Due Years</label>
            </div>
          )}
        </div>

      </div>

      {/* Amounts: show only when owner1 is enabled. amount2 remains visible when owner1 is enabled even if owner2 is unchecked. */}
      {owner1 && (
        <div className="calc-frm-field">
          {/* Amount 1 */}
          <div className="calc-frm-fld form-group sign-of-amount">
            <FloatingNumber
              type="text"
              name="loanInfoMeta.amount"
              control={control}
              placeholder="0.00"
              className="inpt-pla"
              value={getVal('loanInfoMeta.amount', '')}
              onChange={(e) => {
                try {
                  const raw = e.target.value;
                  const amt = parseNumber(raw);
                  // set amount
                  setValue && setValue('loanInfoMeta.amount', raw, { shouldDirty: true, shouldTouch: true });
                  // compute percent from amount if salesPrice present
                  const salesPriceRaw = getVal('salesPrice', '') || '';
                  const spNum = parseNumber(salesPriceRaw);
                  if (spNum > 0) {
                    const perc = (amt / spNum) * 100;
                    // write percent (2 decimals)
                    setValue && setValue('loanInfoMeta.conventionalPercent', (isFinite(perc) ? perc.toFixed(2) : '0.00'), { shouldDirty: true, shouldTouch: true });
                    // update adjusted and notify parent when amount changed
                    try { updateAdjustedAndNotify(); } catch (err) { /* ignore */ }
                  }
                } catch (err) { /* ignore */ }
              }}
              onBlur={() => {
                try {
                  handleCurrencyBlur('loanInfoMeta.amount');
                } catch (err) { /* ignore */ }
              }}
            />
            <label>Amount</label>
          </div>
          {/* Amount 2 */}
          <div className="calc-frm-fld form-group sign-of-amount">
            <FloatingNumber
              type="text"
              name="loanInfoMeta.amount2"
              control={control}
              placeholder="0.00"
              className="inpt-pla"
              value={getVal('loanInfoMeta.amount2', '')}
              onChange={(e) => {
                try {
                  const raw = e.target.value;
                  const amt2 = parseNumber(raw);
                  // set amount2
                  setValue && setValue('loanInfoMeta.amount2', raw, { shouldDirty: true, shouldTouch: true });
                  // compute percent2 from amount2 if salesPrice present
                  const salesPriceRaw = getVal('salesPrice', '') || '';
                  const spNum = parseNumber(salesPriceRaw);
                  if (spNum > 0) {
                    const perc2 = (amt2 / spNum) * 100;
                    setValue && setValue('loanInfoMeta.conventionalPercent2', (isFinite(perc2) ? perc2.toFixed(2) : '0.00'), { shouldDirty: true, shouldTouch: true });
                    // update adjusted and notify parent when amount2 changed
                    try { updateAdjustedAndNotify(); } catch (err) { /* ignore */ }
                  }
                } catch (err) { /* ignore */ }
              }}
              onBlur={() => {
                try {
                  handleCurrencyBlur('loanInfoMeta.amount2');
                } catch (err) { /* ignore */ }
              }}
            />
            <label>Amount</label>
          </div>

        </div>
      )}
    </>
  );
}
